import React, { useContext, useEffect, useRef } from "react"
import { extractTrackingForCRM, hubspotSubmitForm, isProductionHubspot, trackError } from "../../utility/crm/hubspot"
import { Formik } from "formik"
import Form, { FormDoubleColumn } from "../basics/form/Form"
import Name from "../basics/form/field/Name"
import Phone from "../basics/form/field/Phone"
import Lastname from "../basics/form/field/Lastname"
import BusinessEmail from "../basics/form/field/BusinessEmail"
import Company from "../basics/form/field/Company"
import Website from "../basics/form/field/Website"
import Revenue from "../basics/form/field/Revenue"
import Product from "../basics/form/field/Product"
import Country from "../basics/form/field/Country"
import CompanySector from "../basics/form/field/CompanySector"
import Message from "../basics/form/field/Message"
import Submit from "../basics/form/input/Submit"
import PrivacyLegal from "../basics/form/field/PrivacyLegal"
import PrivacyConsent from "../basics/form/field/PrivacyConsent"
import { navigate } from "gatsby"
import { useTranslation } from "react-i18next"
import { PageContext } from "../layout/Page"
import { create } from 'zustand'
import cx from "classnames"
import { createRandomString } from "../../utility/mixed"
import { sha1 } from 'js-sha1';
import { getTrackingCookies } from "../../utility/cookie"
import ReCAPTCHA2 from "../basics/form/input/ReCAPTCHA"
import PropTypes from 'prop-types'

const FormCommercialeMultistepTapToPay = ({ negative = false, customValues = {}, redirect = "/", containerClass = "" }) => {
  const { setData } = useFormStore()
  useEffect(() => {
    setData({ ...customValues, ...{ "prodotto": "SoftPOS" } })
  }, [])


  return <div className={cx(containerClass, "border border-midnight-40 rounded text-midnight-10 overflow-auto")} >
    <StepCompany />
    <div className="h-[1px] bg-midnight-40" />
    <StepContact />
    <div className="h-[1px] bg-midnight-40" />
    <StepSolution redirect={redirect} />
  </div>

}

const StepCompany = () => {
  const { t } = useTranslation("forms")
  const { data, setData, setStep, alreadyVisited, addAlreadyVisited } = useFormStore()
  const current = useFormStore((state) => state.currentStep) === 1

  return <div className={cx("px-5 py-3", { "bg-blue-60": !current, "bg-white": current })} >
    {!current && <PastStep step={1}>
      <span className="font-medium">{t("multistep.companyInformation")}:</span> {data["0-2/name"]}
    </PastStep>}
    {current && <Formik
      onSubmit={(values) => {
        addAlreadyVisited(1)
        setData(values)
        setStep(2)
      }}
      initialValues={{
        "0-2/name": data["0-2/name"],
        "0-2/website": data["0-2/website"],
        fatturato: data.fatturato,
        lista_di_paesi: data.lista_di_paesi,
        "0-2/tipo_di_azienda": data["0-2/tipo_di_azienda"]
      }}
    >
      {({ validateForm, isValid, dirty, ...all }) => {
        return <Form>
          <StepIcon step={1}>{t("multistep.companyInformation")}</StepIcon>
          <Company />
          <Website />
          <CompanySector />
          <Country />
          <Revenue />
          <Submit disabled={isValid && (dirty || alreadyVisited.indexOf(1) !== -1) ? false : true}>{t("multistep.next")}</Submit>
        </Form>
      }}
    </Formik>}
  </div >
}

const StepContact = () => {
  const { t } = useTranslation("forms")
  const { data, setData, setStep, currentStep, alreadyVisited, addAlreadyVisited } = useFormStore()
  const current = useFormStore((state) => state.currentStep) === 2

  return <div className={cx("px-5 py-3", { "bg-blue-60": !current, "bg-white": current })} >
    {!current && currentStep < 2 && <StepIcon step={2} future>
      <span className="font-medium">{t("multistep.contactInformation")}</span>
    </StepIcon>}
    {!current && currentStep > 2 && <PastStep step={2}>
      <span className="font-medium">{t("multistep.contactInformation")}:</span> {data["firstname"]} {data["lastname"]}
    </PastStep>}
    {current && <Formik
      onSubmit={(values) => {
        addAlreadyVisited(2)
        setData(values)
        setStep(3)
      }}
      initialValues={{
        firstname: data.firstname,
        lastname: data.lastname,
        phone: data.phone,
        email: data.email,
      }}
    >
      {({ isValid, dirty }) => {
        return <Form hubspotFormId={isProductionHubspot() ? "8a509e65-5a90-40ad-8731-31da838e8f65" : "20e19f4f-cf6a-4606-8e54-79681ab23567"}>
          <StepIcon step={2}>{t("multistep.contactInformation")}</StepIcon>
          <FormDoubleColumn>
            <Name />
            <Lastname />
          </FormDoubleColumn>
          <BusinessEmail hubspotEmailValidation />
          <Phone />
          <Submit disabled={isValid && (dirty || alreadyVisited.indexOf(2) !== -1) ? false : true}>{t("multistep.next")}</Submit>
        </Form>
      }}
    </Formik>}
  </div>
}

const StepSolution = ({ redirect }) => {
  const honeyPot = useRef()
  const { t } = useTranslation("forms")
  const { country, lang } = useContext(PageContext)
  const { data, setData, alreadyVisited, addAlreadyVisited } = useFormStore()
  const current = useFormStore((state) => state.currentStep) === 3

  return <div className={cx("px-5 py-3", { "bg-blue-60": !current, "bg-white": current })} >
    {!current && <StepIcon step={3} future>
      <span className="font-medium">{t("multistep.solution")}</span>
    </StepIcon>}
    {current && <Formik
      onSubmit={(values) => {
        addAlreadyVisited(3)
        setData(values)
        try {
          if (honeyPot.current.value !== "") {
            throw new Error("Honey has been stoolen (spam)");
          }
          const hash = createRandomString(20);
          const { clickid } = getTrackingCookies()
          const body = {
            ...data,
            ...values,
            "hs_language": lang,
            "contact_channel": "Contact Form Website",
            "last_impact_custid": hash,
            "last_impact_clickid": clickid ? clickid : "-",
            ...extractTrackingForCRM("Sales form")
          }

          hubspotSubmitForm(body, "8a509e65-5a90-40ad-8731-31da838e8f65", "20e19f4f-cf6a-4606-8e54-79681ab23567").then(value => {
            if (value) {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                'event': 'generate_lead',
                'eventName': 'generate_lead',
                'formName': `fs_Informazioni commerciali_${country}_${values.prodotto}`,
                'actionTrackerId': '48001',
                'customerEmail': sha1(body.email),
                'customerId': hash
              })
              navigate(redirect)
            } else {
              trackError('form-commerciale-multistep', "Errore chiamata api Hubspot")
            }
          })
        } catch (e) {
          trackError('form-commerciale-multistep', e)
        }
      }}
      initialValues={{
        prodotto: data.prodotto,
        message: data.message,
        "privacy_marketing": data["privacy_marketing"],
        "privacy_consent": data["privacy_consent"],
        "recaptcha": false
      }}
    >
      {({ isValid, dirty, values }) => {
        return <Form hubspotFormId={isProductionHubspot() ? "8a509e65-5a90-40ad-8731-31da838e8f65" : "20e19f4f-cf6a-4606-8e54-79681ab23567"}>
          <StepIcon step={3}>{t("multistep.solution")}</StepIcon>
          <Product values={{ "SoftPOS": "Tap to Pay" }} disabled={true} />

          <label className="input-name" htmlFor="name">Name</label>
          <input className="input-name" autoComplete="off" type="text" id="name" name="name" placeholder="Your name here" ref={honeyPot}></input>

          <Message required={false} />
          <PrivacyLegal />
          <PrivacyConsent />
          <ReCAPTCHA2 />
          <Submit disabled={isValid && (dirty || alreadyVisited.indexOf(3) !== -1) ? false : true} className="w-full">{t("multistep.submit")}</Submit>
        </Form >
      }}
    </Formik >
    }
  </div >
}

StepSolution.propTypes = {
  redirect: PropTypes.string
}

const useFormStore = create((set) => ({
  currentStep: 1,
  alreadyVisited: [],
  isProdottoPrefilled: true,
  data: {
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    "0-2/name": "",
    "0-2/website": "",
    fatturato: undefined,
    prodotto: undefined,
    lista_di_paesi: undefined,
    "0-2/tipo_di_azienda": undefined,
    message: "",
    "privacy_marketing": false,
    "privacy_consent": false,
  },
  setData: (data) => set((state) => ({
    ...state,
    data: {
      ...state.data,
      ...data
    }
  })),
  setStep: (step) => set((state) => ({ ...state, currentStep: step })),
  setIsProdottoPreFilled: (flag = true) => set((state) => ({ ...state, isProdottoPrefilled: flag })),
  addAlreadyVisited: (step) => set((state) => {
    let visited = state.alreadyVisited
    if (state.alreadyVisited.indexOf(step) === -1) {
      visited.push(step)
    }

    return { ...state, alreadyVisited: visited }
  }),
}))

const StepIcon = ({ step, future = false, children }) => {
  return <div className="flex justify-start items-center gap-2 font-medium">
    <div className={cx("h-[32px] w-[32px] flex justify-center items-center rounded-[4px]", { "bg-blue-50": !future, "bg-white": future })}>
      <span className="text-blue-10 text-medium">{step}</span>
    </div>
    {children}
  </div>
}

StepIcon.propTypes = {
  step: PropTypes.number,
  future: PropTypes.bool,
  children: PropTypes.object
}

const PastStep = ({ step, children }) => {
  const { t } = useTranslation("forms")
  const { setStep } = useFormStore()

  return <div className="flex justify-start items-center gap-2 cursor-pointer" onClick={() => setStep(step)} role="none">
    <div className="h-[32px] w-[32px] min-w-[32px] bg-blue-50 flex justify-center items-center rounded-[4px]">
      <img alt="check" src="/images/icons/checked.svg" />
    </div>
    <div className="w-full">{children}</div>
    <div className="cursor-pointer justify-self-end"><u>{t("multistep.edit")}</u></div>
  </div>
}

PastStep.propTypes = {
  step: PropTypes.number,
  children: PropTypes.object
}

export default FormCommercialeMultistepTapToPay

FormCommercialeMultistepTapToPay.propTypes = {
  negative: PropTypes.bool,
  customValues: PropTypes.object,
  redirect: PropTypes.string,
  containerClass: PropTypes.string
}